import React, { useState, useEffect } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Container,
  Divider,
  Grid,
  IconButton,
  Paper,
  Skeleton,
  TextField,
  ThemeProvider,
  Toolbar,
  Typography,
  createTheme,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import {
  createVehicleLinkClient,
  createVehicleLinkSeller,
  getVehicleById,
} from "../../../api/vehicleApi";
import { formatDate, formatDateTime } from "../../../services/formatDate";
import {
  ClienteData,
  CreateSellerData,
  EditClienteData,
} from "../../../types/clientData";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  createClientWithVehicle,
  createSellerWithVehicle,
  fetchClientByDocument,
  fetchClientByDocumentApiConfig,
  getClientById,
  getClients,
  getClientsCompany,
} from "../../../api/clientApi";
import { formatDocument, formatPhone } from "../../../services/formatDocument";
import { LoadingButton } from "@mui/lab";
import {
  subscribeToChannel,
  triggerEvent,
} from "../../../services/pusherService";

interface PusherData {
  OS: string;
  NOME_CLIENTE: string;
  PESSOACONSULTOR: string;
  DATAFECHAMENTO: string;
  TELEFONE_CONTATO_CLIENTE?: string;
  DOCUMENTO_IDENTIFICADOR_CLIENTE?: number | string;
}

const defaultTheme = createTheme({
  typography: {
    fontFamily: "Poppins, sans-serif",
  },
});

const styles = {
  formControl: {
    fullWidth: true,
    marginTop: -2,
    "& .MuiSelect-root": {
      borderColor: "#303030",
      borderRadius: 3,
      boxShadow: 6,
      "&:focus": {
        backgroundColor: "#ffddec",
        borderColor: "brown",
      },
      "&:before": {
        borderColor: "orange",
      },
      "&:after": {
        borderColor: "green",
      },
    },
    ".MuiOutlinedInput-notchedOutline": {
      borderColor: "#303030",
      borderRadius: 3,
      boxShadow: 6,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#303030",
      color: "#303030",
      borderWidth: "2px",
    },
  },
  button: {
    height: 50,
    marginTop: 6,
    backgroundColor: "#303030",
    borderRadius: 2,
    alignItems: "center",
    ":hover": {
      bgcolor: "#5c5c5c",
    },
  },
};

interface FormValues {
  [key: string]: string;
}

const fieldData = [
  { label: "Nome", name: "nome_cliente", placeholder: "Digite o nome" },
  {
    label: "Documento Identificador",
    name: "documento_identificador_cliente",
    placeholder: "Digite o documento",
  },
  {
    label: "Telefone de Contato",
    name: "telefone_contato_cliente",
    placeholder: "Digite o telefone",
  },
];

interface FieldMappings {
  [key: string]: string;
}
const fieldMappings: FieldMappings = {
  data_recebimento_veiculo: "Data de Recebimento",
  codigo_radio_veiculo: "Código de Rádio",
  data_armazenagem_veiculo: "Data de Armazenagem",
  descricao_cor: "Cor",
  km_veiculo: "Km Inicial",
  km_final_veiculo: "Km Final",
  descricao_modelo: "Modelo",
  descricao_local: "Local de Armazenagem",
  descricao_ano_modelo: "Ano/Modelo",
  created_at: "Data de Criação",
  empresa_nome: "Empresa Localizada",
  permite_fotos_veiculo: "Permite uso das Fotos",
  placa_veiculo: "Placa do Veículo",
  condicao_checklist_respondido: "Condição Comercial",
};

const AddClientPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const { vehicleId } = useParams<{ vehicleId: string }>();
  const token = localStorage.getItem("token");
  const [clients, setClients] = useState<ClienteData[]>([]);
  const [selectedClient, setSelectedClient] = useState<ClienteData | null>(
    null
  );
  const [clientDetails, setClientDetails] = useState<ClienteData | null>(null);
  const [showGrid, setShowGrid] = useState(false);
  const [showSelect, setShowSelect] = useState(false);
  const canal = localStorage.getItem("canal");

  const [formValues, setFormValues] = useState<FormValues>({
    nome_cliente: "",
    documento_identificador_cliente: "",
    telefone_contato_cliente: "",
  });
  const [, setFieldsCompleted] = useState(false);
  const [fieldsDisabled, setFieldsDisabled] = useState(false);
  const [vehicleData, setVehicleData] = useState<any>(null);
  const [showSellerGrid, setShowSellerGrid] = useState(false);
  const [showSellerSelect, setShowSellerSelect] = useState(false);
  const [selectedSeller, setSelectedSeller] = useState<ClienteData | null>(
    null
  );
  const [sellerDetails, setSellerDetails] = useState<ClienteData | null>(null);
  const [hasApiConfig, setHasApiConfig] = useState(false);

  useEffect(() => {
    const canal = localStorage.getItem("canal");
    const eventsString = localStorage.getItem("events");
    const events = eventsString ? JSON.parse(eventsString) : [];

    const hasClientEvent = events.some(
      (event: any) => event.categoria === "cliente"
    );

    if (canal && hasClientEvent) {
      setFieldsDisabled(true);
    } else {
      setFieldsDisabled(false);
    }
  }, []);

  useEffect(() => {
    const userDataString = localStorage.getItem("userData");
    if (userDataString) {
      try {
        const parsed = JSON.parse(userDataString);
        if (parsed.ApiConfig) {
          setHasApiConfig(true);
        }
      } catch (e) {
        console.error("Erro ao ler userData:", e);
      }
    }
  }, []);

  useEffect(() => {
    const fetchClients = async (companyId?: string) => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          throw new Error("Token de autenticação não encontrado");
        }
        const clientsData = companyId
          ? await getClientsCompany(token, companyId)
          : await getClients(token);
        setClients(clientsData);
      } catch (error) {
        console.error("Erro ao buscar clientes:", error);
      }
    };

    const fetchVehicleData = async () => {
      try {
        if (token && vehicleId !== undefined) {
          const data = await getVehicleById(token, vehicleId);
          setVehicleData(data);

          if (data.id_empresa) {
            fetchClients(data.id_empresa);
          } else {
            fetchClients();
          }
        } else {
          console.error("Token não encontrado ou vehicleId é indefinido");
        }
      } catch (error) {
        console.error("Erro ao buscar detalhes do veículo:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchVehicleData();
  }, [token, vehicleId]);

  const handleClientSelect = async (client: ClienteData | null) => {
    try {
      setSelectedClient(client);

      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Token de autenticação não encontrado");
      }

      const clientDetailsData = await getClientById(token, String(client?.id));
      setClientDetails(clientDetailsData);
    } catch (error) {
      console.error("Erro ao buscar detalhes do cliente:", error);
    }
  };

  const handleSellerSelect = async (seller: ClienteData | null) => {
    try {
      setSelectedSeller(seller);

      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Token de autenticação não encontrado");
      }

      const sellerDetailsData = await getClientById(token, String(seller?.id));
      setSellerDetails(sellerDetailsData);
    } catch (error) {
      console.error("Erro ao buscar detalhes do vendedor:", error);
    }
  };

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    fieldName: string
  ) => {
    const { name, value } = event.target;

    let formattedValue =
      name === "documento_identificador_cliente"
        ? formatDocument(value)
        : name === "telefone_contato_cliente"
        ? formatPhone(value)
        : value;

    if (name === "telefone_contato_cliente") {
      formattedValue = formattedValue.slice(0, 14);
    }

    setFormValues((prevValues) => ({
      ...prevValues,
      [fieldName]: formattedValue,
    }));

    const allFieldsCompleted = Object.values(formValues).every(
      (value) => value.trim() !== ""
    );
    setFieldsCompleted(allFieldsCompleted);
  };

  const handleVincularCliente = async () => {
    try {
      if (!token) {
        throw new Error("Token de autenticação não está disponível.");
      }

      if (!vehicleId || !selectedClient?.id) {
        throw new Error("ID do veículo ou do cliente não disponível.");
      }

      await createVehicleLinkClient(selectedClient, token, vehicleId);
      toast.success("Cliente vinculado ao veículo com sucesso!");

      navigate("/veiculos");
    } catch (error) {
      toast.error("Erro ao vincular cliente ao veículo.");
      console.error("Erro ao vincular cliente ao veículo:", error);
    }
  };

  const handleVincularVendedor = async () => {
    try {
      if (!token) {
        throw new Error("Token de autenticação não está disponível.");
      }

      if (!vehicleId || !selectedSeller?.id) {
        throw new Error("ID do veículo ou do vendedor não disponível.");
      }

      await createVehicleLinkSeller(selectedSeller, token, vehicleId);
      toast.success("Vendedor vinculado ao veículo com sucesso!");
      navigate("/veiculos");
    } catch (error) {
      toast.error("Erro ao vincular vendedor ao veículo.");
      console.error("Erro ao vincular vendedor ao veículo:", error);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoadingSubmit(true);
    try {
      if (vehicleId === undefined) {
        throw new Error("ID do veículo não está disponível.");
      }

      if (!token) {
        throw new Error("Token de autenticação não está disponível.");
      }

      const data: EditClienteData = {
        nome_cliente: formValues.nome_cliente,
        documento_identificador_cliente:
          formValues.documento_identificador_cliente,
        telefone_contato_cliente: formValues.telefone_contato_cliente,
      };

      await createClientWithVehicle(data, token, vehicleId);
      toast.success("Cliente cadastrado com sucesso!");
      navigate("/veiculos");
    } catch (error) {
      toast.error("Erro ao cadastrar cliente.");
    } finally {
      setLoadingSubmit(false);
    }
  };

  const handleSubmitSeller = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoadingSubmit(true);
    try {
      if (vehicleId === undefined) {
        throw new Error("ID do veículo não está disponível.");
      }

      if (!token) {
        throw new Error("Token de autenticação não está disponível.");
      }

      const data: CreateSellerData = {
        nome_cliente: formValues.nome_cliente,
        documento_identificador_cliente:
          formValues.documento_identificador_cliente,
        telefone_contato_cliente: formValues.telefone_contato_cliente,
        id_empresa: vehicleData.id_empresa,
      };

      await createSellerWithVehicle(data, token, vehicleId);
      toast.success("Vendedor cadastrado com sucesso!");
      navigate("/veiculos");
    } catch (error) {
      toast.error("Erro ao cadastrar vendedor.");
      console.error("Erro ao cadastrar vendedor:", error);
    } finally {
      setLoadingSubmit(false);
    }
  };

  const handleVincularNovoCliente = () => {
    setShowGrid(true);
    setShowSelect(false);
  };

  const handleVincularClienteExistente = () => {
    setShowSelect(true);
    setShowGrid(false);
  };

  const handleVincularNovoVendedor = () => {
    setShowSellerGrid(true);
    setShowSellerSelect(false);
    setShowGrid(false);
    setShowSelect(false);
  };

  const handleVincularVendedorExistente = () => {
    setShowSellerSelect(true);
    setShowSellerGrid(false);
    setShowGrid(false);
    setShowSelect(false);
  };

  const removeSpecialCharacters = (value: string) => {
    return value.replace(/[.\-/]/g, "");
  };

  useEffect(() => {
    const documento = formValues.documento_identificador_cliente;
    const documentoLimpo = removeSpecialCharacters(documento);
    const eventsString = localStorage.getItem("events");
    const events = eventsString ? JSON.parse(eventsString) : [];
    const hasClientEvent = events.some(
      (event: any) => event.categoria === "cliente"
    );

    if (
      (documentoLimpo.length === 11 || documentoLimpo.length === 14) &&
      canal &&
      hasClientEvent &&
      (showGrid || showSelect)
    ) {
      const buscarCliente = async () => {
        try {
          if (!token) {
            throw new Error("Token de autenticação não está disponível.");
          }

          const clientData = await fetchClientByDocument(documentoLimpo, token);
          if (clientData) {
            triggerEvent(canal, "cliente", clientData);
          }
        } catch (error) {
          console.error("Erro ao buscar dados do cliente:", error);
        }
      };

      buscarCliente();
    }
  }, [
    formValues.documento_identificador_cliente,
    token,
    canal,
    showGrid,
    showSelect,
  ]);

  useEffect(() => {
    if (!hasApiConfig) return;
    if (!token) return;

    const documento = formValues.documento_identificador_cliente;
    if (!documento) return;

    const documentoLimpo = removeSpecialCharacters(documento);

    if (documentoLimpo.length !== 11 && documentoLimpo.length !== 14) {
      return;
    }

    if (!showGrid && !showSellerGrid) return;

    const buscarClienteApiConfig = async () => {
      try {
        const data = await fetchClientByDocumentApiConfig(
          documentoLimpo,
          token
        );

        if (!data) return;

        setFormValues((prev) => ({
          ...prev,
          documento_identificador_cliente: formatDocument(
            data.documento_identificador_cliente || documentoLimpo
          ),
          nome_cliente: data.nome_cliente || prev.nome_cliente,
          telefone_contato_cliente: data.telefone_contato_cliente
            ? formatPhone(data.telefone_contato_cliente)
            : prev.telefone_contato_cliente,
        }));
      } catch (error) {
        console.error("Erro ao buscar cliente via ApiConfig:", error);
        toast.error("Erro ao buscar cliente na API.");
      }
    };

    buscarClienteApiConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formValues.documento_identificador_cliente,
    hasApiConfig,
    token,
    showGrid,
    showSellerGrid,
  ]);

  useEffect(() => {
    if (!canal) {
      console.warn("Canal não definido - assinatura cancelada");
      return;
    }

    const unsubscribe = subscribeToChannel(canal, "cliente", (data: any) => {
      try {
        let parsedData: PusherData;
        if (typeof data === "string") {
          parsedData = JSON.parse(data) as PusherData;
        } else {
          parsedData = data;
        }

        if (parsedData) {
          setFormValues((prevValues) => ({
            ...prevValues,
            nome_cliente: parsedData.NOME_CLIENTE || prevValues.nome_cliente,
            telefone_contato_cliente:
              formatPhone(parsedData.TELEFONE_CONTATO_CLIENTE?.trim() ?? "") ||
              prevValues.telefone_contato_cliente,
            documento_identificador_cliente: parsedData.DOCUMENTO_IDENTIFICADOR_CLIENTE
              ? formatDocument(String(parsedData.DOCUMENTO_IDENTIFICADOR_CLIENTE))
              : prevValues.documento_identificador_cliente,
          }));
        }
      } catch (error) {
        console.error("Erro ao processar dados do Pusher:", error);
      }
    });

    return () => unsubscribe();
  }, [canal]);

  return (
    <ThemeProvider theme={defaultTheme}>
      <Box sx={{ display: "flex" }}>
        <Box
          sx={{
            flexGrow: 1,
            overflow: "auto",
          }}
        >
          <Toolbar sx={{ mb: 2, mt: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                "&:hover": {
                  cursor: "pointer",
                  color: "red",
                },
              }}
              onClick={() => navigate("/veiculos")}
            >
              <IconButton
                edge="start"
                color="inherit"
                aria-label="voltar"
                sx={{ color: "inherit" }}
              >
                <ArrowBack />
              </IconButton>
              <Typography
                variant="h6"
                component="div"
                sx={{ color: "inherit", mt: 0.2 }}
              >
                Voltar
              </Typography>
            </Box>
          </Toolbar>

          <Container sx={{ mb: 4 }} maxWidth={"lg"}>
            <Box component="form" noValidate>
              <Typography
                variant="h5"
                fontStyle={"inherit"}
                sx={{ fontWeight: 400 }}
              >
                Adicionar Cliente ao Veículo
                <Divider
                  variant="middle"
                  sx={{
                    width: 120,
                    backgroundColor: "red",
                    height: 2,
                    marginLeft: 0,
                  }}
                />
              </Typography>
              <Grid sx={{ mt: 3 }} p={2} mr={4}>
                <Grid item xs={11} sm={10} md={8} lg={6}>
                  {loading ? (
                    <Skeleton
                      variant="rectangular"
                      height={300}
                      sx={{ borderRadius: 5 }}
                    />
                  ) : (
                    <Paper
                      elevation={4}
                      sx={{ p: 2, mb: 2, borderRadius: 5, width: "100%" }}
                    >
                      {vehicleData && (
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Typography variant="h5" mr={2}>
                              V.I.N:
                            </Typography>
                            <Typography
                              sx={{
                                fontWeight: "bold",
                                typography: { sm: "h5", xs: "h6" },
                              }}
                            >
                              {vehicleData.chassi_veiculo}
                            </Typography>
                          </Box>
                          <Divider sx={{ my: 2 }} />
                          <Grid container spacing={2}>
                            {Object.entries(vehicleData).map(([key, value]) => {
                              const mappedFieldName = fieldMappings[key] || key;

                              const excludedFields = [
                                "id",
                                "chassi_veiculo",
                                "id_cor",
                                "id_ano_modelo",
                                "id_cliente",
                                "acoes",
                                "fotos_km",
                                "id_modelo",
                                "id_local",
                                "fotos_codigo_radio",
                                "entrega_veiculo",
                                "ano_veiculo",
                                "id_empresa",
                                "transferencia_veiculo",
                                "tem_checklist_agrupado",
                                "id_cliente_vendedor",
                                "valor_checklist_respondido",
                                "vendedor",
                                "recusa_veiculo",
                              ];

                              if (
                                !excludedFields.includes(key) &&
                                value !== null &&
                                value !== ""
                              ) {
                                let formattedValue: string;
                                if (typeof value === "string") {
                                  if (key === "data_recebimento_veiculo") {
                                    formattedValue = formatDateTime(value);
                                  } else if (
                                    key === "data_armazenagem_veiculo"
                                  ) {
                                    formattedValue = formatDate(value);
                                  } else if (key === "created_at") {
                                    formattedValue = formatDateTime(value);
                                  } else {
                                    formattedValue = value;
                                  }
                                } else if (typeof value === "boolean") {
                                  formattedValue = value ? "Sim" : "Não";
                                } else {
                                  formattedValue = String(value);
                                }

                                return (
                                  <Grid item xs={12} sm={6} md={6} key={key}>
                                    <Typography sx={{ fontWeight: "bold" }}>
                                      {mappedFieldName}:
                                    </Typography>
                                    <Typography variant="body1">
                                      {formattedValue}
                                    </Typography>
                                  </Grid>
                                );
                              }
                              return null;
                            })}
                          </Grid>
                        </Box>
                      )}
                    </Paper>
                  )}
                </Grid>
              </Grid>
              {vehicleData?.vendedor && (
                <Grid p={2} mr={4}>
                  <Grid item xs={11} sm={10} md={8} lg={8}>
                    <Paper
                      elevation={4}
                      sx={{ p: 2, mb: 2, borderRadius: 5, width: "100%" }}
                    >
                      <Typography variant="h6" gutterBottom>
                        Vendedor Vinculado
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography sx={{ fontWeight: "bold" }}>
                            Nome:
                          </Typography>
                          <Typography>
                            {vehicleData.vendedor.nome_cliente}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography sx={{ fontWeight: "bold" }}>
                            Documento:
                          </Typography>
                          <Typography>
                            {formatDocument(
                              vehicleData.vendedor
                                .documento_identificador_cliente
                            )}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography sx={{ fontWeight: "bold" }}>
                            Telefone:
                          </Typography>
                          <Typography>
                            {formatPhone(
                              vehicleData.vendedor.telefone_contato_cliente
                            )}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                </Grid>
              )}
              <Grid container spacing={2} p={2} mt={-6}>
                <Grid
                  item
                  md={
                    vehicleData?.placa_veiculo && !vehicleData?.vendedor ? 3 : 6
                  }
                  sm={6}
                  xs={12}
                >
                  {loading ? (
                    <Skeleton
                      variant="rectangular"
                      height={50}
                      sx={{ borderRadius: 5, mt: 5 }}
                    />
                  ) : (
                    <Button
                      variant="contained"
                      sx={styles.button}
                      fullWidth
                      onClick={handleVincularNovoCliente}
                    >
                      Vincular novo cliente
                    </Button>
                  )}
                </Grid>

                <Grid
                  item
                  md={
                    vehicleData?.placa_veiculo && !vehicleData?.vendedor ? 3 : 6
                  }
                  sm={6}
                  xs={12}
                >
                  {loading ? (
                    <Skeleton
                      variant="rectangular"
                      height={50}
                      sx={{ borderRadius: 5, mt: 5 }}
                    />
                  ) : (
                    <Button
                      variant="contained"
                      sx={styles.button}
                      fullWidth
                      onClick={handleVincularClienteExistente}
                    >
                      Vincular cliente existente
                    </Button>
                  )}
                </Grid>

                {vehicleData?.placa_veiculo && !vehicleData?.vendedor && (
                  <>
                    <Grid item md={3} sm={6} xs={12}>
                      {loading ? (
                        <Skeleton
                          variant="rectangular"
                          height={50}
                          sx={{ borderRadius: 5, mt: 5 }}
                        />
                      ) : (
                        <Button
                          variant="contained"
                          sx={styles.button}
                          fullWidth
                          onClick={handleVincularNovoVendedor}
                        >
                          Vincular novo vendedor
                        </Button>
                      )}
                    </Grid>
                    <Grid item md={3} sm={6} xs={12}>
                      {loading ? (
                        <Skeleton
                          variant="rectangular"
                          height={50}
                          sx={{ borderRadius: 5, mt: 5 }}
                        />
                      ) : (
                        <Button
                          variant="contained"
                          sx={styles.button}
                          fullWidth
                          onClick={handleVincularVendedorExistente}
                        >
                          Vincular vendedor existente
                        </Button>
                      )}
                    </Grid>
                  </>
                )}
              </Grid>

              {showSelect && (
                <Grid container spacing={3} mt={2} p={2}>
                  <Grid item xs={12}>
                    <Typography
                      variant="h5"
                      gutterBottom
                      textAlign={"center"}
                      mb={2}
                    >
                      Vincular Cliente Existente
                    </Typography>
                    <div>
                      <Autocomplete
                        id="client-select"
                        options={clients}
                        getOptionLabel={(option) => option.nome_cliente}
                        onChange={(event, value) => handleClientSelect(value)}
                        isOptionEqualToValue={(options, value) =>
                          options.valueOf === value.valueOf
                        }
                        noOptionsText="Nenhum cliente encontrado"
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Selecione o cliente"
                            variant="outlined"
                          />
                        )}
                      />

                      {clientDetails && (
                        <Paper elevation={3} sx={{ p: 2, mb: 2, mt: 3 }}>
                          <Typography variant="h6" gutterBottom>
                            Detalhes do Cliente
                          </Typography>
                          <Box>
                            <Typography variant="body1" gutterBottom>
                              Nome: {clientDetails.nome_cliente}
                            </Typography>
                            <Typography variant="body1" gutterBottom>
                              Documento Identificador:{" "}
                              {clientDetails.documento_identificador_cliente}
                            </Typography>
                            <Typography variant="body1" gutterBottom>
                              Telefone de Contato:{" "}
                              {clientDetails.telefone_contato_cliente}
                            </Typography>
                          </Box>
                        </Paper>
                      )}
                    </div>
                  </Grid>
                  <Grid item md={12} sm={12} xs={12}>
                    <LoadingButton
                      variant="contained"
                      fullWidth
                      sx={styles.button}
                      loading={loadingSubmit}
                      onClick={handleVincularCliente}
                    >
                      <Typography>Vincular Cliente</Typography>
                    </LoadingButton>
                  </Grid>
                </Grid>
              )}

              {showGrid && (
                <>
                  <Typography
                    variant="h5"
                    gutterBottom
                    textAlign={"center"}
                    mt={7}
                  >
                    Vincular Novo Cliente
                  </Typography>
                  <Grid
                    container
                    spacing={3}
                    mt={-2}
                    p={2}
                    sx={styles.formControl}
                  >
                    {fieldData.map((field) => (
                      <Grid item xs={12} sm={6} key={field.name}>
                        <Typography ml={1} mb={-1}>
                          {field.label}
                        </Typography>
                        <TextField
                          fullWidth
                          margin="normal"
                          placeholder={field.placeholder}
                          name={field.name}
                          value={formValues[field.name]}
                          onChange={(e) => handleChange(e, field.name)}
                          disabled={
                            fieldsDisabled &&
                            (field.name === "nome_cliente" ||
                              field.name === "telefone_contato_cliente")
                          }
                          sx={{
                            "& .MuiInputLabel-root.Mui-focused": {
                              color: "#303030",
                            },
                            "& .MuiInputLabel-root": {
                              borderColor: "#303030",
                            },
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 3,
                              boxShadow: 6,
                              "& fieldset": {
                                borderColor: "#303030",
                              },
                            },
                            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                              {
                                borderColor: "#303030",
                              },
                          }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                  <LoadingButton
                    variant="contained"
                    fullWidth
                    loading={loadingSubmit}
                    sx={styles.button}
                    onClick={handleSubmit}
                  >
                    <Typography>Vincular Cliente</Typography>
                  </LoadingButton>
                </>
              )}
              {showSellerSelect && (
                <Grid container spacing={3} mt={2} p={2}>
                  <Grid item xs={12}>
                    <Typography
                      variant="h5"
                      gutterBottom
                      textAlign={"center"}
                      mb={2}
                    >
                      Vincular Vendedor Existente
                    </Typography>
                    <div>
                      <Autocomplete
                        id="seller-select"
                        options={clients}
                        getOptionLabel={(option) => option.nome_cliente}
                        onChange={(event, value) => handleSellerSelect(value)}
                        isOptionEqualToValue={(options, value) =>
                          options.valueOf === value.valueOf
                        }
                        noOptionsText="Nenhum vendedor encontrado"
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Selecione o vendedor"
                            variant="outlined"
                          />
                        )}
                      />

                      {sellerDetails && (
                        <Paper elevation={3} sx={{ p: 2, mb: 2, mt: 3 }}>
                          <Typography variant="h6" gutterBottom>
                            Detalhes do Vendedor
                          </Typography>
                          <Box>
                            <Typography variant="body1" gutterBottom>
                              Nome: {sellerDetails.nome_cliente}
                            </Typography>
                            <Typography variant="body1" gutterBottom>
                              Documento Identificador:{" "}
                              {sellerDetails.documento_identificador_cliente}
                            </Typography>
                            <Typography variant="body1" gutterBottom>
                              Telefone de Contato:{" "}
                              {sellerDetails.telefone_contato_cliente}
                            </Typography>
                          </Box>
                        </Paper>
                      )}
                    </div>
                  </Grid>
                  <Grid item md={12} sm={12} xs={12}>
                    <LoadingButton
                      variant="contained"
                      fullWidth
                      sx={styles.button}
                      loading={loadingSubmit}
                      onClick={handleVincularVendedor}
                    >
                      <Typography>Vincular Vendedor</Typography>
                    </LoadingButton>
                  </Grid>
                </Grid>
              )}

              {showSellerGrid && (
                <>
                  <Typography
                    variant="h5"
                    gutterBottom
                    textAlign={"center"}
                    mt={7}
                  >
                    Vincular Novo Vendedor
                  </Typography>
                  <Grid
                    container
                    spacing={3}
                    mt={-2}
                    p={2}
                    sx={styles.formControl}
                  >
                    {fieldData.map((field) => (
                      <Grid item xs={12} sm={6} key={field.name}>
                        <Typography ml={1} mb={-1}>
                          {field.label}
                        </Typography>
                        <TextField
                          fullWidth
                          margin="normal"
                          placeholder={field.placeholder}
                          name={field.name}
                          value={formValues[field.name]}
                          onChange={(e) => handleChange(e, field.name)}
                          sx={{
                            "& .MuiInputLabel-root.Mui-focused": {
                              color: "#303030",
                            },
                            "& .MuiInputLabel-root": {
                              borderColor: "#303030",
                            },
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 3,
                              boxShadow: 6,
                              "& fieldset": {
                                borderColor: "#303030",
                              },
                            },
                            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                              {
                                borderColor: "#303030",
                              },
                          }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                  <LoadingButton
                    variant="contained"
                    fullWidth
                    loading={loadingSubmit}
                    sx={styles.button}
                    onClick={handleSubmitSeller}
                  >
                    <Typography>Vincular Vendedor</Typography>
                  </LoadingButton>
                </>
              )}
            </Box>
          </Container>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default AddClientPage;
